<script setup lang="ts">
import CommonDialog from '@/components/common/dialog/CommonDialog.vue'
import { BFormInput } from 'bootstrap-vue-next'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { ref } from 'vue'
import { useMemberRegistrationModal } from '@/composables/memberManagement/useMemberRegistrationModal'
import { setLoadingView } from '@/features/miiaFw/utils/miiaFwUtils'
import { toInternational } from '@/utils/converter/phoneNumberConverter'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { PHONE_NUMBER_LENGTH } from '@/constants/userManagement/validation'

const commonDialog = ref<InstanceType<typeof CommonDialog> | null>(null)
const { openModal, errors, setAccount } = useMemberRegistrationModal()

// フォームの入力値
const userName = ref('')
const phoneNumber = ref('')

/**
 * 登録ボタン押下時に呼ばれる関数。
 * 入力値のバリデーションを行い、エラーがなければ setAccount を呼んで登録処理を実行する。
 * 登録処理成功後はモーダルを閉じる。
 */
async function onSubmit() {
  errors.value = []
  // バリデーション
  if (userName.value.length > 60) {
    errors.value.push('氏名は60文字以内で入力してください。')
  }
  if (!phoneNumber.value.trim()) {
    errors.value.push('電話番号は必須です。')
  }
  if (errors.value.length > 0) {
    return
  }
  try {
    setLoadingView(true)
    await setAccount(toInternational(phoneNumber.value), userName.value)
    // 登録処理成功後に明示的にモーダルを閉じる処理
    commonDialog.value?.handleOkConfirmed()
  } catch (e) {
    throw GlobalError.chainError(e, ERROR_CODES._811302_CREATE_USER_ERROR)
  } finally {
    setLoadingView(false)
  }
}

defineExpose({
  openModal
})
</script>
<template>
  <CommonDialog
    size="md"
    ref="commonDialog"
    okTitle="登録"
    cancelTitle="閉じる"
    :centered="true"
    :isEmitOkEvent="true"
    @ok-event="onSubmit"
  >
    <template #header> <h3 class="w-100 text-center">新規ユーザー登録</h3></template>
    <div class="container">
      <div class="d-flex align-items-center pb-3">
        <div class="item-label"><label>氏名</label></div>
        <div class="item-input">
          <BFormInput
            type="text"
            class="form-input"
            v-model="userName"
            placeholder="例）山田太郎 / YamadaTaro123"
          ></BFormInput>
        </div>
      </div>
      <div class="d-flex align-items-center pb-3">
        <div class="item-label">
          <label
            >電話番号<span class="required-icon">
              <FontAwesomeIcon :icon="['fas', 'asterisk']" /> </span
          ></label>
        </div>
        <div class="item-input">
          <BFormInput
            type="text"
            class="form-input"
            v-model="phoneNumber"
            placeholder="例）09012345678"
            :maxLength="PHONE_NUMBER_LENGTH"
          ></BFormInput>
        </div>
      </div>
      <div v-if="errors.length" class="mb-3">
        <ul class="text-danger">
          <li v-for="(error, idx) in errors" :key="idx">{{ error }}</li>
        </ul>
      </div>
      <div>
        <span class="required-icon"> <FontAwesomeIcon :icon="['fas', 'asterisk']" /></span
        >のある項目は必須で入力してください
      </div>
    </div>
  </CommonDialog>
</template>

<style lang="scss" scoped>
.item-label {
  margin: 0;
  width: 15%;
}
.required-icon {
  color: #dc3545;
}
.item-input {
  width: 85%;
}
.form-input {
  color: #212529;
}
</style>
